import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPlatformConfig } from '../../../shared/config/platformConfig';
import { checkCustomerStatus } from '../services/customersApi';
import {
    EMAIL_REGEX,
    validateEmail,
    validatePhone,
    validateName,
    isValidBirthday,
} from '../../../shared/utils/validators';
import { normalizeEmail } from '../../../shared/utils/stringUtils';
import { formatMonthName } from '../../../shared/utils/dateFormatters';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('CustomerForm');

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export default function CustomerForm() {
    const [email, setEmail] = useState('');
    const [phone_number, setPhone_number] = useState('');
    const [name, setName] = useState('');
    const [birthMonth, setBirthMonth] = useState('');
    const [birthDay, setBirthDay] = useState('');
    const [platform, setPlatform] = useState('');
    const [optInMarketing, setOptInMarketing] = useState(false);
    const [errors, setErrors] = useState({});
    const [passUrl, setPassUrl] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [emailChecked, setEmailChecked] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [customerStatus, setCustomerStatus] = useState(null); // 'hasPass', 'exists', 'new', or null

    const query = useQuery();
    const signupToken = query.get('c');

    const normalizedEmail = useMemo(() => normalizeEmail(email), [email]);

    const validate = () => {
        const newErrors = {};

        if (!signupToken) {
            newErrors.form = 'Invalid signup link. Please scan the QR code again.';
        }

        const nameValidation = validateName(name);
        if (!nameValidation.valid) {
            newErrors.name = nameValidation.error;
        }

        const emailValidation = validateEmail(normalizedEmail);
        if (!emailValidation.valid) {
            newErrors.email = emailValidation.error;
        }

        const phoneValidation = validatePhone(phone_number);
        if (!phoneValidation.valid) {
            newErrors.phone_number = phoneValidation.error;
        }

        if (!platform) {
            newErrors.platform = 'Platform is required.';
        }

        if (!isValidBirthday(birthMonth, birthDay)) {
            newErrors.birthday = 'Please choose a valid birthday.';
        }

        if (!optInMarketing) {
            newErrors.optInMarketing = 'You must agree to receive marketing messages.';
        }

        return newErrors;
    };

    const setFieldError = (field, message) => {
        setErrors((prev) => ({ ...prev, [field]: message }));
    };

    const clearFieldError = (field) => {
        setErrors((prev) => {
            const copy = { ...prev };
            delete copy[field];
            return copy;
        });
    };

    const handleEmailCheck = async () => {
        if (!normalizedEmail || !EMAIL_REGEX.test(normalizedEmail)) {
            return;
        }

        if (!signupToken) {
            setErrors({ email: 'Invalid signup link. Please scan the QR code again.' });
            return;
        }

        setCheckingEmail(true);
        try {
            const res = await checkCustomerStatus(normalizedEmail, signupToken);

            // Customer exists or is new
            setCustomerStatus(res.data.status);
            setEmailChecked(true);
            setErrors({});

            if (res.data.status === 'exists' && res.data.customer) {
                // Pre-fill form with existing customer data
                const customer = res.data.customer;
                setName(customer.name || '');
                setPhone_number(customer.phone || '');
                if (customer.birthdayMonth) {
                    setBirthMonth(customer.birthdayMonth);
                }
                if (customer.birthdayDay) {
                    setBirthDay(customer.birthdayDay);
                }
                setOptInMarketing(customer.marketing_opt_in || false);

                // Show info message about existing birthday
                if (customer.birthdayMonth && customer.birthdayDay) {
                    const monthName = formatMonthName(customer.birthdayMonth);
                    setErrors({
                        birthday: `We found your account. Your existing birthday (${monthName} ${parseInt(customer.birthdayDay)}) will be used for this pass.`,
                    });
                }
            } else if (res.data.status === 'new') {
                // Clear form for new customer
                setName('');
                setPhone_number('');
                setBirthMonth('');
                setBirthDay('');
                setOptInMarketing(false);
            }
        } catch (err) {
            // Handle 400 response (customer already has a pass) as a valid response
            if (err.response && err.response.status === 400 && err.response.data) {
                const errorData = err.response.data;
                // Check if this is the "hasPass" status or just a generic 400
                if (errorData.status === 'hasPass' || errorData.message) {
                    setCustomerStatus('hasPass');
                    setErrors({
                        email: errorData.message || 'You already have a pass with this business.',
                    });
                    setEmailChecked(false);
                } else {
                    // Generic 400 error
                    logger.error('Error checking customer status', { error: err, status: err.response?.status });
                    setErrors({
                        email:
                            errorData.message ||
                            'Error checking customer status. Please try again.',
                    });
                    setEmailChecked(false);
                }
            } else {
                // Network or other error
                logger.error('Error checking customer status', { error: err });
                setErrors({
                    email: 'Error checking customer status. Please try again.',
                });
                setEmailChecked(false);
            }
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!emailChecked) {
            setErrors({ email: 'Please check the email first.' });
            return;
        }

        if (customerStatus === 'hasPass') {
            return;
        }

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});

        try {
            const birthDate = birthMonth && birthDay ? `2000-${birthMonth}-${birthDay}` : null;

            const platformConfig = getPlatformConfig(platform);
            if (!platformConfig) {
                throw new Error('Unsupported platform');
            }

            const res = await platformConfig.passCreatorLink(
                normalizedEmail,
                phone_number,
                name.trim(),
                birthDate,
                platform,
                optInMarketing,
                signupToken
            );

            setPassUrl(res.data.passUrl);
            setSubmitted(true);
        } catch (err) {
            setErrors({ form: 'Failed to generate pass' });
        }
    };

    return (
        <div className="p-6 max-w-3xl mx-auto">
            <div className="space-y-4 bg-white shadow-md rounded-xl p-6 mb-8">
                <h2 className="text-2xl font-bold text-primary">Customer Registration Form</h2>
                <p className="text-gray-600">
                    Please fill out this form to register for our loyalty program.
                </p>

                {!submitted ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Email Input - Always Visible */}
                        <div>
                            <label className="block font-medium mb-1">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    className="flex-1 p-3 border border-gray-300 rounded-md"
                                    placeholder="john.doe@example.com"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setEmailChecked(false);
                                        setCustomerStatus(null);
                                        setErrors({});
                                    }}
                                    onBlur={handleEmailCheck}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleEmailCheck();
                                        }
                                    }}
                                    disabled={checkingEmail}
                                />
                                <button
                                    type="button"
                                    onClick={handleEmailCheck}
                                    disabled={
                                        checkingEmail ||
                                        !normalizedEmail ||
                                        !EMAIL_REGEX.test(normalizedEmail)
                                    }
                                    className="px-4 py-3 bg-primary text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {checkingEmail ? 'Checking...' : 'Check'}
                                </button>
                            </div>
                            {errors.email && (
                                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                            )}
                            {customerStatus === 'exists' && (
                                <p className="text-blue-600 text-sm mt-1 bg-blue-50 p-2 rounded">
                                    We found your account. Your information has been pre-filled.
                                </p>
                            )}
                            {customerStatus === 'new' && (
                                <p className="text-green-600 text-sm mt-1 bg-green-50 p-2 rounded">
                                    Please fill in your information.
                                </p>
                            )}
                        </div>

                        {/* Rest of form - only show after email is checked */}
                        {emailChecked && customerStatus !== 'hasPass' && (
                            <>
                                {/* Name */}
                                <div>
                                    <label className="block font-medium mb-1">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`w-full p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder="John Doe"
                                        value={name}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            if (e.target.value.trim().length >= 2) {
                                                clearFieldError('name');
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!name.trim()) {
                                                setFieldError('name', 'Name is required.');
                                            } else if (name.trim().length < 2) {
                                                setFieldError(
                                                    'name',
                                                    'Name must be at least 2 characters.'
                                                );
                                            } else {
                                                clearFieldError('name');
                                            }
                                        }}
                                        disabled={customerStatus === 'exists'}
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                                    )}
                                    {customerStatus === 'exists' && name && (
                                        <p className="text-gray-600 text-xs mt-1 italic">
                                            Name is locked to your account information
                                        </p>
                                    )}
                                </div>

                                {/* Phone (10 digits only) */}
                                <div>
                                    <label className="block font-medium mb-1">Phone #</label>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        className={`w-full p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder="1234567890"
                                        value={phone_number}
                                        maxLength={10}
                                        onChange={(e) => {
                                            // keep digits only, max 10
                                            const digits = e.target.value
                                                .replace(/\D/g, '')
                                                .slice(0, 10);
                                            setPhone_number(digits);

                                            if (digits.length === 0) {
                                                clearFieldError('phone_number'); // optional field
                                            } else if (digits.length === 10) {
                                                clearFieldError('phone_number');
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!phone_number) {
                                                clearFieldError('phone_number'); // optional
                                                return;
                                            }
                                            if (phone_number.length !== 10) {
                                                setFieldError(
                                                    'phone_number',
                                                    'Phone number must be exactly 10 digits.'
                                                );
                                            } else {
                                                clearFieldError('phone_number');
                                            }
                                        }}
                                        disabled={customerStatus === 'exists'}
                                    />
                                    {errors.phone_number && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.phone_number}
                                        </p>
                                    )}
                                    {customerStatus === 'exists' && phone_number && (
                                        <p className="text-gray-600 text-xs mt-1 italic">
                                            Phone number is locked to your account information
                                        </p>
                                    )}
                                </div>

                                {/* Birthday */}
                                <div>
                                    <label className="block font-medium mb-1">Birthday:</label>
                                    <div className="flex space-x-2">
                                        <select
                                            value={birthMonth}
                                            onChange={(e) => {
                                                setBirthMonth(e.target.value);
                                                // revalidate birthday on change
                                                if (isValidBirthday(e.target.value, birthDay)) {
                                                    clearFieldError('birthday');
                                                }
                                            }}
                                            className={`w-1/2 p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                            disabled={customerStatus === 'exists'}
                                        >
                                            <option value="">Month</option>
                                            {Array.from({ length: 12 }, (_, i) => {
                                                const monthNumber = i + 1; // 1-12
                                                const monthValue = String(monthNumber).padStart(
                                                    2,
                                                    '0'
                                                ); // "01" to "12"
                                                const monthName = formatMonthName(i + 1);
                                                return (
                                                    <option key={monthNumber} value={monthValue}>
                                                        {monthName}
                                                    </option>
                                                );
                                            })}
                                        </select>

                                        <select
                                            value={birthDay}
                                            onChange={(e) => {
                                                setBirthDay(e.target.value);
                                                if (isValidBirthday(birthMonth, e.target.value)) {
                                                    clearFieldError('birthday');
                                                }
                                            }}
                                            className={`w-1/2 p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                            disabled={customerStatus === 'exists'}
                                        >
                                            <option value="">Day</option>
                                            {Array.from({ length: 31 }, (_, i) => (
                                                <option
                                                    key={i + 1}
                                                    value={String(i + 1).padStart(2, '0')}
                                                >
                                                    {i + 1}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.birthday && (
                                        <p className="text-blue-600 text-sm mt-1 bg-blue-50 p-2 rounded">
                                            {errors.birthday}
                                        </p>
                                    )}
                                    {customerStatus === 'exists' && birthMonth && birthDay && (
                                        <p className="text-gray-600 text-xs mt-1 italic">
                                            Birthday is locked to your account information
                                        </p>
                                    )}
                                </div>

                                {/* Platform */}
                                <div>
                                    <label className="block font-medium mb-1">
                                        Platform: <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={platform}
                                        onChange={(e) => {
                                            setPlatform(e.target.value);
                                            if (e.target.value) {
                                                clearFieldError('platform');
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!platform) {
                                                setFieldError('platform', 'Platform is required.');
                                            } else {
                                                clearFieldError('platform');
                                            }
                                        }}
                                        className="w-full p-3 border border-gray-300 rounded-md"
                                    >
                                        <option value="">Select platform</option>
                                        <option value="iphone">iPhone</option>
                                        <option value="android">Android</option>
                                    </select>
                                    {errors.platform && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.platform}
                                        </p>
                                    )}
                                </div>

                                {/* Marketing Opt-in */}
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="optInMarketing"
                                        checked={optInMarketing}
                                        onChange={(e) => {
                                            setOptInMarketing(e.target.checked);
                                            if (e.target.checked) {
                                                clearFieldError('optInMarketing');
                                            }
                                        }}
                                        className="w-5 h-5"
                                    />
                                    <label
                                        htmlFor="optInMarketing"
                                        className="text-sm text-gray-700"
                                    >
                                        I agree to receive marketing messages from this business
                                        and understand how my information is used per the{' '}
                                        <a
                                            href="/privacy-policy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary underline"
                                        >
                                            Privacy Policy
                                        </a>
                                        . <span className="text-red-500">*</span>
                                    </label>
                                </div>
                                {errors.optInMarketing && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.optInMarketing}
                                    </p>
                                )}

                                {/* Form error */}
                                {errors.form && (
                                    <p className="text-red-500 text-sm mt-1">{errors.form}</p>
                                )}

                                <button
                                    type="submit"
                                    className="w-full bg-primary text-white py-3 rounded-md hover:bg-primary transition disabled:opacity-50"
                                    disabled={
                                        !name.trim() ||
                                        !normalizedEmail ||
                                        !optInMarketing ||
                                        !platform
                                    }
                                >
                                    Register
                                </button>
                            </>
                        )}
                    </form>
                ) : (
                    <div className="text-center">
                        <h3 className="text-lg font-semibold mb-2">Thank you for registering!</h3>
                        <p className="mb-4">
                            Click the button below to add your loyalty card to your wallet:
                        </p>

                        {passUrl && (
                            <div className="mb-10 flex justify-center">
                                <a
                                    href={passUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-primary text-white py-3 px-6 rounded-md hover:bg-blue-700 transition inline-block"
                                >
                                    Add to Wallet
                                </a>
                            </div>
                        )}

                        <p className="text-sm text-gray-500">
                            Use your loyalty card at our location to earn rewards.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
